import classNames from 'classnames';
import { scaleLinear } from 'd3';
import clamp from 'lodash/clamp';
import { type CSSProperties, useMemo } from 'react';

import { BAR_END_HOVER_SLOP } from '../../constants';
import { PatternDiagonalStripe } from '../../svg-patterns/patterns';
import {
  findNearestBandIndex,
  getDimmedPlotColor,
  getOtherColor,
  truncateText,
  useChartTheme,
} from '../../utilities';
import axisLabelStyles from '../axis/axis-label.module.css';
import styles from './stacked-bar-chart.module.css';
import { buildGroupAxisLayout } from './stacked-bar-group-axis';
import {
  adjustSegmentSpans,
  findSegmentSpanAtCoord,
} from './stacked-bar-scale';
import {
  buildSegmentEntries,
  Dimensions,
  StackedBarLegend,
  type StackedBarRendererProps,
} from './stacked-bar-shared';
import {
  StackedBarSharedTooltip,
  type StackedBarTooltipState,
  useStackedBarTooltip,
} from './stacked-bar-tooltip';

const EMPTY_BAR_PATTERN_ID = 'diagonal-stripe-4';

export const VerticalStackedBar = ({
  model,
  width,
  height,
  format,
  hideLegend,
  formatLegendLabel,
  maxLegendLabelWidth,
  isTimeseries = false,
  highlightMatchingSegments = true,
}: StackedBarRendererProps) => {
  const {
    dataIndexed,
    displayLabels,
    segmentLabels,
    stackedSeries,
    emptyBars,
    colorScale,
    domainMax,
  } = model;

  const { theme } = useChartTheme();

  const legendHeight = hideLegend
    ? 0
    : Dimensions.LegendHeight + Dimensions.NewLegendTopMargin;
  const labelHeight = Dimensions.XAxisLabelHeight;
  const minWidth = dataIndexed.length * Dimensions.MinBarThickness;
  const maxWidth = dataIndexed.length * Dimensions.MaxBarThickness;
  const plotWidth = clamp(width, minWidth, maxWidth);
  const plotHeight = Math.max(0, height - legendHeight - labelHeight);
  const chartInsetLeft = Math.max(0, (width - plotWidth) / 2);

  const isPlotSizeValid = plotWidth > 0 && plotHeight > 0;
  const shouldHideLegend =
    hideLegend || legendHeight + plotHeight + labelHeight > height;

  const groupAxis = useMemo(
    () =>
      buildGroupAxisLayout({
        labels: dataIndexed.map((d) => d.label),
        isTimeseries,
        rangeLength: plotWidth,
      }),
    [dataIndexed, isTimeseries, plotWidth]
  );
  const yScale = useMemo(
    () => scaleLinear().domain([0, domainMax]).range([plotHeight, 0]),
    [domainMax, plotHeight]
  );
  const barWidth = groupAxis.barThickness;
  const columnSlop = Math.max(0, (groupAxis.step - barWidth) / 2);
  const focusHoveredOnly = !highlightMatchingSegments;

  // Precomputed bar x-positions — reused by hit-testing and the render.
  const barStarts = useMemo(
    () => dataIndexed.map((_, index) => groupAxis.getBarStart(index)),
    [dataIndexed, groupAxis]
  );
  const emptyIndexSet = useMemo(
    () => new Set(emptyBars.map((bar) => bar._index)),
    [emptyBars]
  );

  // Column spans are measured up from the baseline.
  const columnLayouts = useMemo(
    () =>
      dataIndexed.map((_, columnIndex) =>
        adjustSegmentSpans(
          stackedSeries.map((series) => {
            const segment = series[columnIndex];
            return segment
              ? Math.max(0, yScale(segment[0]) - yScale(segment[1]))
              : 0;
          }),
          Dimensions.SegmentSpacing
        )
      ),
    [dataIndexed, stackedSeries, yScale]
  );

  const xAxisEntries = useMemo(
    () =>
      groupAxis.isTime
        ? groupAxis.ticks.map((tick, index) => ({
            key: `tick-${index}`,
            center: tick.coord,
            label: tick.label,
            maxWidth: Math.max(0, groupAxis.tickSpacing - 8),
          }))
        : dataIndexed.map((d, index) => {
            const start = barStarts[index];
            return {
              key: `axis-${index}`,
              center: start === undefined ? undefined : start + barWidth / 2,
              label: displayLabels?.[index] ?? d.label,
              maxWidth: Math.max(barWidth, groupAxis.step),
            };
          }),
    [groupAxis, dataIndexed, displayLabels, barStarts, barWidth]
  );

  // Shared single tooltip: bars along x (band axis), stacked by value along y.
  const hitTest = ([px, py]: [
    number,
    number,
  ]): StackedBarTooltipState | null => {
    const column = findNearestBandIndex(barStarts, barWidth, px, columnSlop);
    if (column === null) return null;

    if (emptyIndexSet.has(column)) {
      return {
        key: `empty:${column}`,
        title: dataIndexed[column]?.label ?? '',
        row: column,
        entries: undefined,
      };
    }

    const layouts = columnLayouts[column];
    if (layouts === undefined) return null;
    // Spans grow up from the baseline; py grows downward.
    const hitIndex = findSegmentSpanAtCoord(
      layouts,
      plotHeight - py,
      BAR_END_HOVER_SLOP
    );
    if (hitIndex === null) return null;
    const hitSeries = stackedSeries[hitIndex];
    const hitSegment = hitSeries?.[column];
    if (hitSeries === undefined || hitSegment === undefined) return null;
    return {
      key: `${column}:${hitSeries.key}`,
      title: hitSeries.key,
      row: column,
      entries: buildSegmentEntries(
        hitSeries,
        hitSegment,
        format,
        focusHoveredOnly
      ),
    };
  };

  const { state, cursor, isVisible, gProps } = useStackedBarTooltip(hitTest);

  const highlightedSegment =
    isVisible && state?.entries !== undefined ? (state?.title ?? null) : null;
  // An empty-column hover ('') matches no series key, so everything dims.
  const hoveredState = isVisible ? state : null;
  const hoveredKey =
    hoveredState === null
      ? null
      : (hoveredState.entries !== undefined && hoveredState.title) || '';
  const hoveredColumn = hoveredState === null ? null : hoveredState.row;

  const barsContent = useMemo(
    () => (
      <>
        {stackedSeries.map((series, seriesIndex) => {
          const isDimmedSeries =
            !focusHoveredOnly &&
            hoveredKey !== null &&
            series.key !== hoveredKey;
          return (
            <g
              key={`series-${series.key}`}
              fill={colorScale(series.key) ?? getOtherColor()}
              style={
                {
                  '--replots-dim-color': getDimmedPlotColor(
                    colorScale(series.key) ?? getOtherColor(),
                    theme
                  ),
                } as CSSProperties
              }
              className={classNames(
                styles.bar,
                isDimmedSeries && styles.seriesDimmed
              )}
            >
              {series.map((segment) => {
                const x = barStarts[segment.data._index];
                if (x === undefined) return null;
                const layout =
                  columnLayouts[segment.data._index]?.[seriesIndex];
                if (layout === undefined || layout.span === 0) return null;
                const yTop = plotHeight - layout.start - layout.span;
                const segmentHeight = Math.max(
                  0,
                  layout.span - Dimensions.SegmentSpacing
                );
                if (segmentHeight === 0) return null;
                const isDimmedSegment =
                  focusHoveredOnly &&
                  hoveredKey !== null &&
                  !(
                    series.key === hoveredKey &&
                    segment.data._index === hoveredColumn
                  );
                return (
                  <rect
                    key={`segment-${series.key}-${segment.data._index}`}
                    x={x}
                    y={yTop}
                    fill={colorScale(series.key)}
                    width={barWidth}
                    height={segmentHeight}
                    className={classNames(
                      styles.segment,
                      isDimmedSegment && styles.segmentDimmed
                    )}
                    rx={0}
                  />
                );
              })}
            </g>
          );
        })}
        {emptyBars.map((emptyBar) => {
          const x = barStarts[emptyBar._index];
          if (x === undefined) return null;
          return (
            <rect
              key={`empty-bar-${emptyBar.label}-${emptyBar._index}`}
              x={x}
              y={0}
              width={barWidth}
              height={plotHeight}
              fill={`url(#${EMPTY_BAR_PATTERN_ID}) #cbd5e0`}
            />
          );
        })}
      </>
    ),
    [
      stackedSeries,
      emptyBars,
      barStarts,
      barWidth,
      columnLayouts,
      colorScale,
      focusHoveredOnly,
      hoveredKey,
      hoveredColumn,
      plotHeight,
      theme,
    ]
  );

  const svgHeight = plotHeight + labelHeight;
  const svgWidth = Math.max(width, plotWidth);

  return (
    <div className={styles.chartVerticalStack}>
      <div className={styles.verticalBarScroll}>
        <div className={styles.plotViewport} style={{ width: svgWidth }}>
          <svg width={svgWidth} height={svgHeight} overflow="visible">
            <defs>
              <PatternDiagonalStripe patternId={EMPTY_BAR_PATTERN_ID} />
            </defs>
            <g id="chart" transform={`translate(${chartInsetLeft}, 0)`}>
              {isPlotSizeValid && (
                <g {...gProps}>
                  {/* Full-plot hit surface: gaps hit-test like bars. */}
                  <rect
                    x={0}
                    y={0}
                    width={plotWidth}
                    height={plotHeight}
                    fill="transparent"
                  />
                  {barsContent}
                </g>
              )}
            </g>
            <g
              id="x-axis"
              transform={`translate(${chartInsetLeft}, ${plotHeight})`}
            >
              {isPlotSizeValid &&
                xAxisEntries.map((entry) =>
                  entry.center === undefined ? null : (
                    <g
                      key={entry.key}
                      transform={`translate(${entry.center}, 6)`}
                    >
                      <text
                        textAnchor="middle"
                        dominantBaseline="hanging"
                        fontSize={11}
                        className={axisLabelStyles.yAxisLabel}
                        data-testid="stacked-vertical-bar-chart-x-axis-label"
                      >
                        <title>{entry.label}</title>
                        {truncateText({
                          text: entry.label,
                          maxWidth: entry.maxWidth,
                        })}
                      </text>
                    </g>
                  )
                )}
            </g>
          </svg>
          <StackedBarSharedTooltip
            state={state}
            cursor={cursor}
            isVisible={isVisible}
          />
        </div>
      </div>
      {!shouldHideLegend && isPlotSizeValid && (
        <StackedBarLegend
          className={styles.legendBelow}
          segmentLabels={segmentLabels}
          colorScale={colorScale}
          formatLegendLabel={formatLegendLabel}
          maxLegendLabelWidth={maxLegendLabelWidth}
          highlightedLabel={highlightedSegment}
        />
      )}
    </div>
  );
};
